import { ISelectedEntity } from './types';
import { z } from 'zod';
import { getSelectedEntityUniqId } from './utils';

/**
 * React Select uses the return value of getOptionValue and getOptionLabel to
 * build React key for the selected options. It claims that getOptionValue and
 * getOptionLabel accept the generic Option type (in our case ISelectedEntity)
 * but this is not the case when we use the creatable version of React Select.
 * getOptionValue and getOptionLabel are called with a value
 * which looks like our ICreatableReactSelectOption (has `__isNew__: true` and
 * typed text in `value`) but the `label` is `JSX.Element`, not `string`
 * as defined in ICreatableReactSelectOption. Perhaps, the `label` is what
 * React Select will display in DOM, wrapped with markup.
 *
 * The getReactSelectOptionId util handles this special case by parsing the input
 * and if it looks like creatable option, returns the `value` to be used as key.
 * Otherwise, it uses our getSelectedEntityUniqId function to return unique
 * identifier of the selected item
 */

//  observed creatable option
const internalReactSelectCreatableOption = z.object({
  __isNew__: z.literal(true),
  value: z.string(),
  label: z.any(),
});

// inferred type
type IInternalReactSelectCreatableOption = z.infer<
  typeof internalReactSelectCreatableOption
>;

// type guard to perform the narrowing
export const isCreatableReactSelectOption = (
  selectedValue: unknown,
): selectedValue is IInternalReactSelectCreatableOption => {
  return internalReactSelectCreatableOption.safeParse(selectedValue).success;
};

export const getReactSelectOptionId = (
  selectedValue: ISelectedEntity | IInternalReactSelectCreatableOption,
): string => {
  if (isCreatableReactSelectOption(selectedValue)) {
    return selectedValue.value;
  } else {
    return getSelectedEntityUniqId(selectedValue);
  }
};
